import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addStuff } from "../../../redux/userRelated/userHandle";
import { underControl } from "../../../redux/userRelated/userSlice";
import Popup from "../../../components/Popup";
import { FEEDBACK, SUCCESS_NAVIGATE_DELAY_MS, formatAdminError, ERROR_HINTS } from "../../../utils/feedbackMessages";
import {
    alpha,
    Box,
    Button,
    Container,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";

const AddNotice = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { status, response, error } = useSelector((state) => state.user);
    const { currentUser } = useSelector((state) => state.user);

    const [title, setTitle] = useState("");
    const [details, setDetails] = useState("");
    const [date, setDate] = useState("");
    const [targetAudience, setTargetAudience] = useState("all");
    const [priority, setPriority] = useState(0);
    const [pinned, setPinned] = useState(false);
    const [sendEmailPush, setSendEmailPush] = useState(true);
    const [sendSmsPush, setSendSmsPush] = useState(true);

    const adminID = currentUser._id;

    const [loader, setLoader] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [message, setMessage] = useState("");

    const fields = {
        title,
        details,
        date,
        adminID,
        targetAudience,
        priority,
        pinned,
        sendEmailPush,
        sendSmsPush,
    };
    const address = "Notice";

    const submitHandler = (event) => {
        event.preventDefault();
        setLoader(true);
        dispatch(addStuff(fields, address));
    };

    useEffect(() => {
        if (status === "added") {
            setMessage(FEEDBACK.noticeAdded);
            setPopupSeverity("success");
            setShowPopup(true);
            setLoader(false);
            dispatch(underControl());
            const timer = setTimeout(() => navigate("/Admin/notices"), SUCCESS_NAVIGATE_DELAY_MS);
            return () => clearTimeout(timer);
        } else if (status === "failed") {
            setMessage(formatAdminError(response, "Could not post the notice. Check the title and message, then try again."));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        } else if (status === "error") {
            setMessage(formatAdminError(error, ERROR_HINTS.network));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        }
    }, [status, navigate, error, response, dispatch]);

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 40%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3 },
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <CampaignOutlinedIcon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Add notice
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Choose audience, priority, and whether to email the selected group (SMTP must be configured on the server).
                            </Typography>
                        </Box>
                    </Stack>

                    <Box component="form" onSubmit={submitHandler}>
                        <Stack spacing={2.25}>
                            <TextField
                                fullWidth
                                required
                                label="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                required
                                multiline
                                minRows={4}
                                label="Details"
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                required
                                type="date"
                                label="Event / publish date"
                                InputLabelProps={{ shrink: true }}
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />

                            <FormControl fullWidth>
                                <InputLabel id="audience-label">Target audience</InputLabel>
                                <Select
                                    labelId="audience-label"
                                    label="Target audience"
                                    value={targetAudience}
                                    onChange={(e) => setTargetAudience(e.target.value)}
                                >
                                    <MenuItem value="all">Everyone (students &amp; teachers)</MenuItem>
                                    <MenuItem value="teacher">Teachers only</MenuItem>
                                    <MenuItem value="student">Students / guardians only</MenuItem>
                                </Select>
                                <FormHelperText>
                                    Students use guardian email on file; teachers use their account email.
                                </FormHelperText>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel id="priority-label">Priority</InputLabel>
                                <Select
                                    labelId="priority-label"
                                    label="Priority"
                                    value={priority}
                                    onChange={(e) => setPriority(Number(e.target.value))}
                                >
                                    <MenuItem value={0}>Normal</MenuItem>
                                    <MenuItem value={1}>Medium</MenuItem>
                                    <MenuItem value={2}>High</MenuItem>
                                    <MenuItem value={3}>Urgent</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={<Switch checked={pinned} onChange={(e) => setPinned(e.target.checked)} color="primary" />}
                                label="Pin to top of lists"
                            />

                            <FormControlLabel
                                control={
                                    <Switch checked={sendEmailPush} onChange={(e) => setSendEmailPush(e.target.checked)} color="primary" />
                                }
                                label="Send email to audience when notice is created"
                            />

                            <FormControlLabel
                                control={
                                    <Switch checked={sendSmsPush} onChange={(e) => setSendSmsPush(e.target.checked)} color="primary" />
                                }
                                label="Send SMS to guardians/teachers with phone numbers on file"
                            />

                            <Grid container spacing={1.5} sx={{ pt: 1 }}>
                                <Grid item xs={6}>
                                    <Button fullWidth variant="outlined" onClick={() => navigate("/Admin/notices")} disabled={loader}>
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button fullWidth type="submit" variant="contained" disabled={loader}>
                                        {loader ? <CircularProgress size={24} color="inherit" /> : "Publish"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
            <Popup
                message={message}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />
        </Box>
    );
};

export default AddNotice;
